import React from 'react'
import Page from "../components/Page";
import { IconArmchair2, IconCarrot, IconChevronRight, IconFriends, IconChartBar } from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { Link } from "react-router-dom";
import { useDashboard } from '../controllers/dashboard.controller';
import { CURRENCIES } from "../config/currencies.config";
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import clsx from 'clsx';
import WeeklySalesChart from '../components/WeeklySalesChart';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { data, error, isLoading } = useDashboard();
  const { theme } = useTheme();

  if(isLoading) {
    return <Page>
      {t('dashboard.loading_message')}
    </Page>
  }

  if(error) {
    console.error(error);
    return <Page>
      {t('dashboard.error')}
    </Page>;
  }

  const {
    reservations, topSellingItems, ordersCount, newCustomerCount, repeatedCustomerCount, currency:currencyCode, todaysRevenue, activeOrdersCount, activeOrdersValuation, weeklySales
  } = data || {};

  const currency = CURRENCIES.find((c)=>c.cc==currencyCode);

  return (
    <Page>
      <h3 className="text-2xl">{t('dashboard.title')}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">

        {/* reservation */}
        <div className="border rounded-3xl h-96 overflow-y-auto border-restro-border-green">
          <div className= "py-5 px-6 border-b-0 backdrop-blur rounded-t-3xl sticky top-0">
            <Link to="/dashboard/reservation" className='font-bold'>{t('dashboard.reservations')}</Link>
          </div>

          {reservations?.length == 0 && <div className='w-full px-8 2xl:px-10 pt-2'>
            <img
              src="/assets/illustrations/reservation-not-found.webp"
              alt="no-reservation"
              className='w-7/12 mx-auto'
            />
            <p className='mt-2 text-center text-gray-500 font-semibold'>Oh snap! 🍽️</p>
            <p className='text-center text-gray-500'>{t('dashboard.no_reservations')}</p>
          </div>}

          {
            reservations?.length > 0 && <div className='px-6 flex flex-col divide-y dark:divide-restro-border-green'>

            {/* item */}
            {reservations.map((reservation,i)=>{

              const {
                id,
                customer_id,
                customer_name,
                date,
                table_id,
                table_title,
                status,
                notes,
                people_count,
                unique_code,
                created_at,
                updated_at,
              } = reservation;

              const formmatedDate = new Intl.DateTimeFormat("en", {dateStyle: "medium", timeStyle: "short"}).format(new Date(date))

              return <div className='py-1' key={i}>
                <p className="text-xs text-gray-500">{formmatedDate}</p>
                <p className='text-base mt-1'>{customer_name} <span className="text-xs text-gray-500">- ({customer_id})</span></p>
                <div className='flex items-center gap-2 mt-1'>
                  <p className="flex items-center gap-2 text-xs">
                    <IconFriends stroke={iconStroke} size={14} /> {people_count} People
                  </p>
                  <p className="flex items-center gap-2 text-xs">
                    <IconArmchair2 stroke={iconStroke} size={14} /> {table_title}
                  </p>
                </div>
              </div>;
            })}
            {/* item */}

          </div>
          }

          <div className="h-6"></div>
        </div>
        {/* reservation */}

        {/* popular items */}
        <div className="border rounded-3xl h-96 overflow-y-auto border-restro-border-green">
          <div className="py-5 px-6 border-b-0 backdrop-blur rounded-t-3xl sticky top-0">
            <h3 className='font-bold'>{t('dashboard.top_selling_items')}</h3>
          </div>

          {topSellingItems?.length == 0 && <div className='w-full px-8 2xl:px-10'>
            <img
              src="/assets/illustrations/top-selling-not-found.webp"
              alt="no-top-selling items"
              className='w-7/12 mx-auto'
            />
            <p className='mt-2 text-center text-gray-500 font-semibold'>Uh-oh! 🚨</p>
            <p className='text-center text-gray-500'>{t('dashboard.no_top_selling_items')}</p>
          </div>}

          {
            topSellingItems?.length > 0 && <div className='px-6 pt-1 flex flex-col'>
            {/* item */}
            {topSellingItems.map((item,i)=>{
              return <div className='mb-4 flex items-center gap-2 w-full' key={i}>
                <div className='flex items-center justify-center w-12 h-12 rounded-lg bg-restro-bg-gray text-restro-text'>
                  <IconCarrot stroke={iconStroke} />
                </div>

                <div className='flex-1'>
                  <p>{item?.title}</p>
                  <p className="text-xs text-gray-500">{currency?.symbol}{item?.price}</p>
                </div>

                <p className='font-bold'>{item?.orders_count}</p>
              </div>;
            })}
            {/* item */}
          </div>
          }
        </div>
        {/* popular items */}


        <div className="flex flex-col gap-6">
          {/* today's revenue */}
          <div className="border rounded-[2rem] h-32 py-5 px-8 border-restro-border-green backdrop-blur bg-restro-green/5 shadow-sm flex flex-col justify-center">
            <h3 className="font-black text-restro-green uppercase text-[10px] tracking-[0.2em] mb-1">{t('dashboard.revenue') || 'Today\'s Revenue'}</h3>
            <p className="text-4xl font-black text-restro-green flex items-baseline gap-1">
              <span className="text-xl opacity-60 font-medium">{currency?.symbol || ''}</span>
              {Number(String(todaysRevenue || 0).replace(/,/g, "")).toLocaleString()}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
             {/* active orders */}
            <Link to="/dashboard/orders" className="border rounded-[2rem] h-32 py-5 px-8 border-amber-500/30 backdrop-blur bg-amber-500/5 shadow-sm flex flex-col justify-center hover:bg-amber-500/10 transition-colors cursor-pointer">
              <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-amber-500/80 mb-1">Active Orders</h3>
              <p className="text-4xl font-black text-amber-500">{activeOrdersCount||0}</p>
            </Link>

            {/* active orders valuation */}
            <Link to="/dashboard/orders" className="border rounded-[2rem] h-32 py-5 px-8 border-amber-500/30 backdrop-blur bg-amber-500/5 shadow-sm flex flex-col justify-center hover:bg-amber-500/10 transition-colors cursor-pointer">
              <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-amber-500/80 mb-1">Active Valuation</h3>
              <p className="text-2xl lg:text-3xl font-black text-amber-500 flex items-baseline gap-1">
                <span className="text-lg opacity-60 font-medium">{currency?.symbol || ''}</span>
                {Number(String(activeOrdersValuation || 0).replace(/,/g, "")).toLocaleString()}
              </p>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-6 mt-6">
             {/* orders */}
            <div className="border rounded-[2rem] h-32 py-5 px-8 border-restro-border-green backdrop-blur shadow-sm flex flex-col justify-center">
              <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1">{t('dashboard.orders')} (Today)</h3>
              <p className="text-4xl font-black">{ordersCount||0}</p>
            </div>

            {/* new customers */}
            <div className="border rounded-[2rem] h-32 py-5 px-8 border-restro-border-green backdrop-blur shadow-sm flex flex-col justify-center">
              <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1">{t('dashboard.new_customers')}</h3>
              <p className="text-4xl font-black">{newCustomerCount||0}</p>
            </div>
          </div>

          {/* repeated customers */}
          <div className="border rounded-[2rem] h-32 py-5 px-8 border-restro-border-green backdrop-blur shadow-sm flex flex-col justify-center">
            <h3 className="font-bold text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1">{t('dashboard.repeat_customers')}</h3>
            <p className="text-4xl font-black">{repeatedCustomerCount||0}</p>
          </div>
        </div>

        {/* banner: view more in reports */}
        <Link 
          to="/dashboard/reports" 
          className="border rounded-3xl p-6 transition-all active:scale-[0.98] border-restro-border-green bg-[url(/assets/circle_illustration.svg)] bg-no-repeat bg-right-bottom hover:bg-restro-green/5 dark:hover:bg-restro-green/10 flex flex-col justify-between shadow-sm cursor-pointer group"
        >
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-restro-green px-2.5 py-0.5 rounded-full bg-restro-green/10 border border-restro-border-green">
                Directory Hub
              </span>
              <IconChartBar size={20} className="text-restro-green" />
            </div>
            <h4 className="text-lg font-black text-restro-text">
              {t('dashboard.view_more_reports')}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Explore 20+ operations, hotel, financial, audit, kitchen, and inventory reports in an interactive grid view.
            </p>
          </div>
          <div className="flex items-center gap-1 text-sm font-bold text-restro-green mt-4 group-hover:translate-x-1 transition-transform">
            <span>Explore All Reports</span>
            <IconChevronRight stroke={iconStroke} size={18} />
          </div>
        </Link>
        {/* banner: view more in reports */}
      </div>

      {/* Down: Weekly Sales Bar Diagram (Brand Themed) */}
      <WeeklySalesChart weeklySales={weeklySales} currency={currency} />
    </Page>
  )
}
